import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Users, Search, Filter, Plus, Star, Phone, MapPin, 
  Tag, Clock, DollarSign, Zap, CreditCard, Edit2, Trash2,
  X, Check, ChevronDown, ArrowLeft, Building2, Calendar
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactPicker from "../components/ContactPicker";
import { RatingDisplay } from "../components/StarRating";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MySubs = ({ user, setUser }) => {
  const [subs, setSubs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [options, setOptions] = useState({
    predefined_tags: [],
    priority_tiers: [],
    price_tiers: [],
    response_times: [],
    payment_terms: []
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTag, setFilterTag] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [editForm, setEditForm] = useState({
    tags: [],
    priority_tier: "none",
    price_tier: "none",
    specialty: "",
    response_time: "none",
    payment_terms: "none",
    notes: "",
    last_used_date: ""
  });
  const [customTag, setCustomTag] = useState("");

  useEffect(() => {
    fetchData();
  }, [filterTag, filterPriority, filterCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch options, categories, and subs in parallel
      const [optionsRes, categoriesRes, subsRes] = await Promise.all([
        axios.get(`${API}/my-subs/options`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/my-subs`, {
          params: {
            tag: filterTag !== "all" ? filterTag : undefined,
            priority: filterPriority !== "all" ? filterPriority : undefined,
            category: filterCategory !== "all" ? filterCategory : undefined
          },
          withCredentials: true
        })
      ]);
      
      setOptions(optionsRes.data);
      setCategories(categoriesRes.data);
      setSubs(subsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load your subs");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sub) => {
    setEditingSub(sub);
    setEditForm({
      tags: sub.tags || [],
      priority_tier: sub.priority_tier || "none",
      price_tier: sub.price_tier || "none",
      specialty: sub.specialty || "",
      response_time: sub.response_time || "none",
      payment_terms: sub.payment_terms || "none",
      notes: sub.notes || "",
      last_used_date: sub.last_used_date || ""
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingSub) return;
    
    try {
      // Convert "none" values back to null for API
      const dataToSend = {
        ...editForm,
        priority_tier: editForm.priority_tier === "none" ? null : editForm.priority_tier,
        price_tier: editForm.price_tier === "none" ? null : editForm.price_tier,
        response_time: editForm.response_time === "none" ? null : editForm.response_time,
        payment_terms: editForm.payment_terms === "none" ? null : editForm.payment_terms,
      };
      
      const res = await axios.put(
        `${API}/my-subs/${editingSub.sub_id}`,
        dataToSend,
        { withCredentials: true }
      );
      
      setSubs(subs.map(s => s.sub_id === editingSub.sub_id ? res.data : s));
      setEditModalOpen(false);
      setEditingSub(null);
      toast.success("Sub updated!");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (subId) => {
    try {
      await axios.delete(`${API}/my-subs/${subId}`, { withCredentials: true });
      setSubs(subs.filter(s => s.sub_id !== subId));
      toast.success("Removed from your subs");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const handleMarkUsed = async (subId) => {
    try {
      const res = await axios.put(
        `${API}/my-subs/${subId}/last-used`,
        {},
        { withCredentials: true }
      );
      setSubs(subs.map(s => 
        s.sub_id === subId 
          ? { ...s, last_used_date: res.data.last_used_date }
          : s
      ));
      toast.success("Marked as used today");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const addTag = (tag) => {
    if (!editForm.tags.includes(tag)) {
      setEditForm({ ...editForm, tags: [...editForm.tags, tag] });
    }
  };

  const removeTag = (tag) => {
    setEditForm({ ...editForm, tags: editForm.tags.filter(t => t !== tag) });
  };

  const addCustomTag = () => {
    if (customTag.trim() && !editForm.tags.includes(customTag.trim())) {
      setEditForm({ ...editForm, tags: [...editForm.tags, customTag.trim()] });
      setCustomTag("");
    }
  };

  // Filter subs by search query
  const filteredSubs = subs.filter(sub => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sub.service?.name?.toLowerCase().includes(query) ||
      sub.specialty?.toLowerCase().includes(query) ||
      sub.tags?.some(t => t.toLowerCase().includes(query))
    );
  });

  // Get all unique tags used
  const allUsedTags = [...new Set(subs.flatMap(s => s.tags || []))];

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.category_id === categoryId);
    return cat?.name || categoryId;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Primary": return "bg-green-100 text-green-700 border-green-200";
      case "Backup": return "bg-blue-100 text-blue-700 border-blue-200";
      case "Occasional": return "bg-stone-100 text-stone-600 border-stone-200";
      default: return "bg-stone-100 text-stone-600 border-stone-200";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Header user={user} />
        <div className="pt-24 flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="my-subs-page">
      <Header user={user} />

      <main className="pt-24 lg:pt-28 pb-16 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1A4D2E] mb-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Link>
              <h1 className="text-2xl lg:text-3xl font-bold text-[#1A1A1A] flex items-center gap-3">
                <Users className="w-8 h-8 text-[#1A4D2E]" />
                My Subs
              </h1>
              <p className="text-[#71717A] mt-1">
                {subs.length} subcontractor{subs.length !== 1 ? 's' : ''} in your network
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <ContactPicker 
                user={user} 
                categories={categories} 
                onContactAdded={() => fetchData()}
                defaultAction="mysubs"
              />
              <Button
                asChild
                className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full"
              >
                <Link to="/directory" data-testid="browse-directory-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Browse Directory
                </Link>
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  type="text"
                  placeholder="Search by name, specialty, or tag..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-full border-stone-200"
                  data-testid="search-subs-input"
                />
              </div>
              
              {/* Filter by Tag */}
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger className="w-full lg:w-48 rounded-full" data-testid="filter-tag">
                  <Tag className="w-4 h-4 mr-2 text-[#71717A]" />
                  <SelectValue placeholder="Filter by tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allUsedTags.map(tag => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Filter by Priority */}
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-full lg:w-48 rounded-full" data-testid="filter-priority">
                  <Zap className="w-4 h-4 mr-2 text-[#71717A]" />
                  <SelectValue placeholder="Filter by priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {options.priority_tiers.map(tier => (
                    <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Filter by Category */}
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full lg:w-48 rounded-full" data-testid="filter-category">
                  <Filter className="w-4 h-4 mr-2 text-[#71717A]" />
                  <SelectValue placeholder="Filter by trade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Trades</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subs List */}
          {filteredSubs.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
              <Users className="w-16 h-16 text-stone-200 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">
                {subs.length === 0 ? "No subs yet" : "No matches found"}
              </h3>
              <p className="text-[#71717A] mb-6">
                {subs.length === 0 
                  ? "Start building your subcontractor network by browsing the directory."
                  : "Try adjusting your search or filters."}
              </p>
              {subs.length === 0 && (
                <Button asChild className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full">
                  <Link to="/directory">Browse Directory</Link>
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredSubs.map((sub) => (
                <div
                  key={sub.sub_id}
                  className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 hover:border-[#1A4D2E]/30 transition-colors"
                  data-testid={`sub-card-${sub.sub_id}`}
                >
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Image */}
                    <div className="w-full lg:w-32 h-32 rounded-xl bg-stone-100 overflow-hidden flex-shrink-0">
                      {sub.service?.image_url ? (
                        <img
                          src={sub.service.image_url}
                          alt={sub.service.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-10 h-10 text-stone-300" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <Link
                              to={`/service/${sub.service_id}`}
                              className="text-xl font-bold text-[#1A1A1A] hover:text-[#1A4D2E] transition-colors"
                            >
                              {sub.service?.name}
                            </Link>
                            {sub.priority_tier && (
                              <Badge className={`${getPriorityColor(sub.priority_tier)} border`}>
                                {sub.priority_tier}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-[#71717A] mb-3">
                            <span className="flex items-center gap-1">
                              <Filter className="w-4 h-4" />
                              {getCategoryName(sub.service?.category_id)}
                            </span>
                            {sub.service?.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {sub.service.city}
                              </span>
                            )}
                            {sub.service?.phone && (
                              <a 
                                href={`tel:${sub.service.phone}`}
                                className="flex items-center gap-1 hover:text-[#1A4D2E]"
                              >
                                <Phone className="w-4 h-4" />
                                {sub.service.phone}
                              </a>
                            )}
                            <RatingDisplay
                              rating={sub.service?.average_rating || 0}
                              reviewCount={sub.service?.review_count || 0}
                              size="sm"
                            />
                          </div>

                          {/* Tags */}
                          {sub.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {sub.tags.map(tag => (
                                <Badge
                                  key={tag}
                                  variant="secondary"
                                  className="bg-[#1A4D2E]/10 text-[#1A4D2E] border-0"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}

                          {/* Details Row */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            {sub.price_tier && (
                              <span className="flex items-center gap-1 text-[#4A4A4A]">
                                <DollarSign className="w-4 h-4 text-[#E07A5F]" />
                                {sub.price_tier}
                              </span>
                            )}
                            {sub.response_time && (
                              <span className="flex items-center gap-1 text-[#4A4A4A]">
                                <Zap className="w-4 h-4 text-amber-500" />
                                {sub.response_time} response
                              </span>
                            )}
                            {sub.payment_terms && (
                              <span className="flex items-center gap-1 text-[#4A4A4A]">
                                <CreditCard className="w-4 h-4 text-[#1A4D2E]" />
                                {sub.payment_terms}
                              </span>
                            )}
                            {sub.last_used_date && (
                              <span className="flex items-center gap-1 text-[#4A4A4A]">
                                <Calendar className="w-4 h-4 text-blue-500" />
                                Last used: {new Date(sub.last_used_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          {/* Specialty */}
                          {sub.specialty && (
                            <p className="text-sm text-[#4A4A4A] mt-2 italic">
                              Specialty: {sub.specialty}
                            </p>
                          )}

                          {/* Notes */}
                          {sub.notes && (
                            <p className="text-sm text-[#71717A] mt-2 bg-stone-50 p-2 rounded-lg">
                              {sub.notes}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex lg:flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkUsed(sub.sub_id)}
                            className="border-[#1A4D2E] text-[#1A4D2E] rounded-full"
                            data-testid={`mark-used-${sub.sub_id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Used Today
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(sub)}
                            className="rounded-full"
                            data-testid={`edit-sub-${sub.sub_id}`}
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full"
                                data-testid={`remove-sub-${sub.sub_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Remove
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove from My Subs?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will remove {sub.service?.name} from your subcontractor list. You can always add them back later.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(sub.sub_id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editingSub?.service?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Tags */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Tags</Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {editForm.tags.map(tag => (
                  <Badge
                    key={tag}
                    className="bg-[#1A4D2E] text-white cursor-pointer hover:bg-[#143D24]"
                    onClick={() => removeTag(tag)}
                  >
                    {tag}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {options.predefined_tags
                  .filter(t => !editForm.tags.includes(t))
                  .map(tag => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="cursor-pointer hover:bg-[#1A4D2E]/10"
                      onClick={() => addTag(tag)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add custom tag..."
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomTag())}
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={addCustomTag}>
                  Add
                </Button>
              </div>
            </div>

            {/* Tier Selectors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="priority">Priority Tier</Label>
                <Select
                  value={editForm.priority_tier}
                  onValueChange={(v) => setEditForm({ ...editForm, priority_tier: v })}
                >
                  <SelectTrigger id="priority" className="mt-1.5">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {options.priority_tiers.map(tier => (
                      <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="price">Price Tier</Label>
                <Select
                  value={editForm.price_tier}
                  onValueChange={(v) => setEditForm({ ...editForm, price_tier: v })}
                >
                  <SelectTrigger id="price" className="mt-1.5">
                    <SelectValue placeholder="Select price tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {options.price_tiers.map(tier => (
                      <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="response">Response Time</Label>
                <Select
                  value={editForm.response_time}
                  onValueChange={(v) => setEditForm({ ...editForm, response_time: v })}
                >
                  <SelectTrigger id="response" className="mt-1.5">
                    <SelectValue placeholder="Select response time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {options.response_times.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="payment">Payment Terms</Label>
                <Select
                  value={editForm.payment_terms}
                  onValueChange={(v) => setEditForm({ ...editForm, payment_terms: v })}
                >
                  <SelectTrigger id="payment" className="mt-1.5">
                    <SelectValue placeholder="Select payment terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {options.payment_terms.map(term => (
                      <SelectItem key={term} value={term}>{term}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Specialty */}
            <div>
              <Label htmlFor="specialty">Specialty / Best At</Label>
              <Input
                id="specialty"
                value={editForm.specialty}
                onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })}
                placeholder="e.g., Commercial HVAC installs, Bathroom remodels"
                className="mt-1.5"
              />
            </div>

            {/* Last Used Date */}
            <div>
              <Label htmlFor="lastUsed">Last Used Date</Label>
              <Input
                id="lastUsed"
                type="date"
                value={editForm.last_used_date}
                onChange={(e) => setEditForm({ ...editForm, last_used_date: e.target.value })}
                className="mt-1.5"
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Private Notes</Label>
              <Textarea
                id="notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Add private notes about this sub..."
                className="mt-1.5 min-h-[100px]"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={handleSaveEdit}
                className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full"
              >
                Save Changes
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingSub(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default MySubs;
